import React, { useEffect, useState } from 'react';

const STORAGE_KEY = 'inverow_cookie_consent';

/**
 * Returns the stored consent object, or null if not yet decided.
 * Shape: { version: 1, analytics: bool, marketing: bool, decidedAt: ISO string }
 */
export function getCookieConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveConsent(analytics, marketing) {
  const consent = {
    version: 1,
    analytics,
    marketing,
    decidedAt: new Date().toISOString(),
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
  return consent;
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    // Show banner only if user hasn't decided yet
    if (!getCookieConsent()) {
      // Small delay so the page renders first
      const t = setTimeout(() => setVisible(true), 400);
      return () => clearTimeout(t);
    }
  }, []);

  if (!visible) return null;

  const handleAcceptAll = () => {
    saveConsent(true, true);
    setVisible(false);
  };

  const handleEssentialOnly = () => {
    saveConsent(false, false);
    setVisible(false);
  };

  const handleSaveChoice = () => {
    saveConsent(analytics, marketing);
    setVisible(false);
  };

  return (
    <>
      {/* Backdrop blur for mobile */}
      <div style={backdropStyle} aria-hidden="true" />

      <div role="dialog" aria-label="Cookie settings" style={bannerStyle}>
        {/* Icon + heading */}
        <div style={headerRowStyle}>
          <span style={cookieIconStyle}>🍪</span>
          <div>
            <h2 style={titleStyle}>This website uses cookies</h2>
            <p style={subtitleStyle}>
              We use cookies to ensure the website works correctly and, with your consent,
              for analytics. You can change your preference at any time.
            </p>
          </div>
        </div>

        {/* Expandable detail */}
        {expanded && (
          <div style={detailStyle}>
            {/* Essential */}
            <div style={cookieRowStyle}>
              <div style={cookieRowTextStyle}>
                <strong style={cookieLabelStyle}>Essential cookies</strong>
                <span style={cookieDescStyle}>
                  Enable core functionality such as authentication and session management.
                  The website cannot work without them.
                </span>
              </div>
              <div style={toggleLockedStyle}>Always on</div>
            </div>

            <div style={dividerStyle} />

            {/* Analytics */}
            <div style={cookieRowStyle}>
              <div style={cookieRowTextStyle}>
                <strong style={cookieLabelStyle}>Analytics cookies</strong>
                <span style={cookieDescStyle}>
                  Help us understand how visitors interact with the website (e.g. Google Analytics).
                </span>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={analytics}
                onClick={() => setAnalytics((v) => !v)}
                style={toggleStyle(analytics)}
              >
                <span style={toggleThumbStyle(analytics)} />
              </button>
            </div>

            <div style={dividerStyle} />

            {/* Marketing */}
            <div style={cookieRowStyle}>
              <div style={cookieRowTextStyle}>
                <strong style={cookieLabelStyle}>Marketing cookies</strong>
                <span style={cookieDescStyle}>
                  Used to show relevant advertisements (e.g. Google AdSense).
                </span>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={marketing}
                onClick={() => setMarketing((v) => !v)}
                style={toggleStyle(marketing)}
              >
                <span style={toggleThumbStyle(marketing)} />
              </button>
            </div>
          </div>
        )}

        {/* Actions */}
        <div style={actionsStyle}>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            style={detailButtonStyle}
          >
            {expanded ? 'Hide details ▲' : 'Customize ▼'}
          </button>

          <div style={mainActionsStyle}>
            <button
              type="button"
              onClick={expanded ? handleSaveChoice : handleEssentialOnly}
              style={secondaryButtonStyle}
            >
              {expanded ? 'Save my choice' : 'Essential only'}
            </button>

            <button
              type="button"
              onClick={handleAcceptAll}
              style={primaryButtonStyle}
            >
              Accept all
            </button>
          </div>
        </div>

        {/* Legal note */}
        <p style={legalStyle}>
          See our{' '}
          <a href="/cookies" style={linkStyle}>Cookies notice</a>,{' '}
          <a href="/privacy" style={linkStyle}>Privacy Policy</a>
          {' '}and{' '}
          <a href="/terms" style={linkStyle}>Terms of Service</a>.
        </p>
      </div>
    </>
  );
}

/* ── Styles ── */

const backdropStyle = {
  display: 'none', // only visible on very small screens via media query
};

const bannerStyle = {
  position: 'fixed',
  bottom: '20px',
  left: '50%',
  transform: 'translateX(-50%)',
  width: 'min(680px, calc(100vw - 32px))',
  background: 'var(--popup-bg)',
  border: '1px solid var(--popup-border)',
  borderRadius: '20px',
  boxShadow: '0 16px 48px rgba(0,0,0,0.55)',
  padding: '24px',
  zIndex: 9000,
  display: 'grid',
  gap: '20px',
  backdropFilter: 'blur(16px)',
  WebkitBackdropFilter: 'blur(16px)',
};

const headerRowStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '16px',
};

const cookieIconStyle = {
  fontSize: '32px',
  lineHeight: 1,
  flexShrink: 0,
  marginTop: '2px',
};

const titleStyle = {
  margin: '0 0 6px 0',
  color: 'var(--popup-text)',
  fontSize: '18px',
  fontWeight: 800,
};

const subtitleStyle = {
  margin: 0,
  color: 'var(--text-secondary)',
  fontSize: '14px',
  lineHeight: 1.55,
};

const detailStyle = {
  background: 'var(--popup-item-bg)',
  border: '1px solid var(--popup-item-border)',
  borderRadius: '14px',
  padding: '6px 0',
  display: 'grid',
};

const cookieRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '16px',
  padding: '14px 18px',
};

const cookieRowTextStyle = {
  display: 'grid',
  gap: '3px',
  flex: 1,
};

const cookieLabelStyle = {
  color: 'var(--popup-text)',
  fontSize: '14px',
  fontWeight: 700,
};

const cookieDescStyle = {
  color: 'var(--text-secondary)',
  fontSize: '13px',
  lineHeight: 1.4,
};

const dividerStyle = {
  height: '1px',
  background: 'var(--popup-item-border)',
  margin: '0 18px',
};

const toggleLockedStyle = {
  fontSize: '12px',
  fontWeight: 700,
  color: 'var(--text-secondary)',
  whiteSpace: 'nowrap',
  opacity: 0.7,
};

const toggleStyle = (on) => ({
  width: '44px',
  height: '26px',
  borderRadius: '13px',
  border: 'none',
  background: on ? 'var(--primary)' : 'var(--popup-item-border)',
  cursor: 'pointer',
  position: 'relative',
  flexShrink: 0,
  transition: 'background 0.2s ease',
  padding: 0,
});

const toggleThumbStyle = (on) => ({
  position: 'absolute',
  top: '3px',
  left: on ? '21px' : '3px',
  width: '20px',
  height: '20px',
  borderRadius: '50%',
  background: '#ffffff',
  transition: 'left 0.2s ease',
  display: 'block',
  boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
});

const actionsStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '12px',
  flexWrap: 'wrap',
};

const mainActionsStyle = {
  display: 'flex',
  gap: '10px',
  flexWrap: 'wrap',
};

const detailButtonStyle = {
  border: 'none',
  background: 'transparent',
  color: 'var(--text-secondary)',
  fontSize: '13px',
  fontWeight: 600,
  cursor: 'pointer',
  padding: '8px 4px',
  whiteSpace: 'nowrap',
};

const secondaryButtonStyle = {
  border: '1px solid var(--popup-border)',
  borderRadius: '12px',
  padding: '11px 20px',
  fontWeight: 700,
  fontSize: '14px',
  cursor: 'pointer',
  background: 'transparent',
  color: 'var(--popup-text)',
  whiteSpace: 'nowrap',
};

const primaryButtonStyle = {
  border: 'none',
  borderRadius: '12px',
  padding: '11px 24px',
  fontWeight: 700,
  fontSize: '14px',
  cursor: 'pointer',
  background: 'var(--primary)',
  color: '#ffffff',
  whiteSpace: 'nowrap',
};

const legalStyle = {
  margin: 0,
  color: 'var(--text-secondary)',
  fontSize: '12px',
  lineHeight: 1.5,
};

const linkStyle = {
  color: 'var(--primary)',
  textDecoration: 'underline',
};
